/**
 * Form Field Component
 * Renders the appropriate field component based on field type
 */
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { fieldComponents } from './FormControls'
import { FormFieldProps, FieldType } from './types'

const FormField: React.FC<FormFieldProps> = props => {
  const { config, values } = props
  const { unregister } = useFormContext()

  // Calculate field type dynamically if type is a function
  // MUST be called before any conditional returns to follow React Hooks rules
  const fieldType =
    typeof config.type === 'function' ? config.type(values) : config.type

  // Unregister field when component unmounts (type changes)
  // MUST be called before any conditional returns to follow React Hooks rules
  // This ensures react-hook-form removes the field and its validation state
  // Use keepValue: true to preserve field value when field type changes
  // (e.g., from READONLY to TEXT/SELECT when isEditing changes)
  useEffect(() => {
    return () => {
      // Unregister the field when component unmounts (type changed)
      // Keep the field value to prevent loss when field type changes
      // This ensures values from initialValues are preserved when Controller is re-registered
      try {
        unregister(config.name, { keepValue: true })
      } catch (error) {
        console.error(
          `Error unregistering field ${config.name} on unmount:`,
          error
        )
      }
    }
  }, [config.name, unregister])

  // Check if field should be read-only
  // Priority: 1. Field-level readOnly, 2. Form-level readOnly (boolean or array)
  const isReadOnly =
    config.readOnly === true ||
    (typeof props.readOnly === 'boolean' && props.readOnly) ||
    (Array.isArray(props.readOnly) && props.readOnly.includes(config.name))

  // If field is read-only, render ReadOnly component
  // TODO: DATE_RANGE fields store { start, end } objects but ReadOnlyField only formats
  // scalar values, causing [object Object] display. Need to format DATE_RANGE values
  // before passing to ReadOnlyComponent when readOnly mode is used.
  if (isReadOnly && fieldType !== FieldType.READONLY) {
    const ReadOnlyComponent = fieldComponents[FieldType.READONLY]
    return <ReadOnlyComponent {...props} readOnly={true} />
  }

  // Get the appropriate component for the field type
  const FieldComponent = fieldComponents[fieldType]

  if (!FieldComponent) {
    console.error(`No component found for field type: ${fieldType}`)
    return null
  }

  // Create modified config for fields that don't need dataSource (e.g., DATE_RANGE)
  // Remove dataSource and autoSelectFirst when type is DATE_RANGE
  const modifiedConfig =
    fieldType === FieldType.DATE_RANGE
      ? { ...config, dataSource: undefined, autoSelectFirst: undefined }
      : config

  return (
    <FieldComponent {...props} config={modifiedConfig} readOnly={isReadOnly} />
  )
}

export default FormField
